import { useState, useEffect } from 'react';
import { Dialog } from '@/shared/components/ui/Dialog';
import { useI18n } from '@/shared/i18n/useT';
import { migrateGlobalFormatKey } from '@/shared/formula/globalFormats';
import type {
  SummaryCardConfig,
  SummaryCardCalculationType,
} from './summaryCards';
export interface SummaryCardColumnOption {
  readonly field?: string | undefined;
  readonly headerName?: React.ReactNode | undefined;
}

export interface SummaryCardEditDialogProps {
  readonly card?: SummaryCardConfig | null;
  readonly columns: readonly SummaryCardColumnOption[];
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (card: SummaryCardConfig) => void;
  readonly onDelete?: ((cardId: string) => void) | undefined;
}

export function SummaryCardEditDialog({
  card,
  columns,
  isOpen,
  onClose,
  onSave,
  onDelete,
}: SummaryCardEditDialogProps) {
  const { t } = useI18n();

  const [label, setLabel] = useState('');
  const [calculationType, setCalculationType] = useState<SummaryCardCalculationType>('SUM');
  const [columnId, setColumnId] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [format, setFormat] = useState('integer');
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');
  const [decimals, setDecimals] = useState<string>('');

  useEffect(() => {
    if (card) {
      setLabel(card.label || '');
      setCalculationType(card.calculationType || 'SUM');
      setColumnId(card.columnId || (columns[0]?.field ?? ''));
      setCustomValue(card.customValue || '');
      setFormat(migrateGlobalFormatKey(card.format || 'integer'));
      setPrefix(card.prefix || '');
      setSuffix(card.suffix || '');
      setDecimals(card.decimals !== undefined ? String(card.decimals) : '');
    } else {
      setLabel('');
      setCalculationType('SUM');
      setColumnId(columns[0]?.field ?? '');
      setCustomValue('');
      setFormat('integer');
      setPrefix('');
      setSuffix('');
      setDecimals('');
    }
  }, [card, columns, isOpen]);

  if (!isOpen) return null;

  const isEditing = Boolean(card && card.id);
  const title = isEditing ? t('summaryCards.editCard') : t('summaryCards.addCard');

  const handleSave = () => {
    const trimmedLabel = label.trim();
    const finalLabel = trimmedLabel || (
      calculationType === 'CUSTOM'
        ? t('summaryCards.customCalculation')
        : `${calculationType} ${columns.find((c) => c.field === columnId)?.headerName || columnId}`
    );

    const parsedDecimals = decimals !== '' ? parseInt(decimals, 10) : undefined;
    const finalDecimals = parsedDecimals !== undefined && !isNaN(parsedDecimals) && parsedDecimals >= 0
      ? Math.min(10, parsedDecimals)
      : undefined;

    const newConfig: SummaryCardConfig = {
      id: card?.id || `card_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      label: finalLabel,
      calculationType,
      ...(calculationType !== 'CUSTOM' && columnId ? { columnId } : {}),
      ...(calculationType === 'CUSTOM' && customValue.trim() ? { customValue: customValue.trim() } : {}),
      ...(format ? { format } : {}),
      ...(prefix.trim() ? { prefix: prefix.trim() } : {}),
      ...(suffix.trim() ? { suffix: suffix.trim() } : {}),
      ...(finalDecimals !== undefined ? { decimals: finalDecimals } : {}),
    };

    onSave(newConfig);
    onClose();
  };

  return (
    <Dialog
      title={title}
      description={t('summaryCards.dialogDesc')}
      onClose={onClose}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <div>
            {isEditing && onDelete && card && (
              <button
                type="button"
                onClick={() => {
                  onDelete(card.id);
                  onClose();
                }}
                className="tap-target rounded-full border border-danger/30 px-4 text-xs sm:text-sm font-medium text-danger hover:bg-danger/10 transition-colors"
              >
                {t('summaryCards.deleteCard')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="tap-target rounded-full border border-border px-4 text-xs sm:text-sm font-medium text-fg hover:bg-surface-hover transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="tap-target rounded-full bg-brand px-4 text-xs sm:text-sm font-medium text-on-status hover:bg-brand-hover transition-colors"
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-3 py-1 text-xs sm:text-sm">
        {/* Label */}
        <div className="flex flex-col gap-1">
          <label htmlFor="card-label-input" className="font-semibold text-fg">
            {t('summaryCards.cardLabel')}
          </label>
          <input
            id="card-label-input"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('summaryCards.labelPlaceholder')}
            className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-fg outline-none focus:border-focus focus:ring-1 focus:ring-focus"
          />
        </div>

        {/* Calculation Type */}
        <div className="flex flex-col gap-1">
          <label htmlFor="card-calc-type" className="font-semibold text-fg">
            {t('summaryCards.calculationType')}
          </label>
          <select
            id="card-calc-type"
            value={calculationType}
            onChange={(e) => setCalculationType(e.target.value as SummaryCardCalculationType)}
            className="h-9 w-full rounded-lg border border-border bg-bg px-2.5 text-fg outline-none focus:border-focus focus:ring-1 focus:ring-focus"
          >
            <option value="SUM">{t('summaryCards.calcSum')}</option>
            <option value="AVERAGE">{t('summaryCards.calcAverage')}</option>
            <option value="COUNT">{t('summaryCards.calcCount')}</option>
            <option value="COUNTA">{t('summaryCards.calcCountA')}</option>
            <option value="MIN">{t('summaryCards.calcMin')}</option>
            <option value="MAX">{t('summaryCards.calcMax')}</option>
            <option value="CUSTOM">{t('summaryCards.calcCustom')}</option>
          </select>
        </div>

        {/* Column Picker (if not custom) */}
        {calculationType !== 'CUSTOM' ? (
          <div className="flex flex-col gap-1">
            <label htmlFor="card-column-select" className="font-semibold text-fg">
              {t('summaryCards.selectedColumn')}
            </label>
            <select
              id="card-column-select"
              value={columnId}
              onChange={(e) => setColumnId(e.target.value)}
              className="h-9 w-full rounded-lg border border-border bg-bg px-2.5 text-fg outline-none focus:border-focus focus:ring-1 focus:ring-focus"
            >
              {columns.map((col) => (
                <option key={col.field} value={col.field}>
                  {col.headerName || col.field}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <label htmlFor="card-custom-val" className="font-semibold text-fg">
              {t('summaryCards.customValueOrFormula')}
            </label>
            <input
              id="card-custom-val"
              type="text"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              placeholder="e.g. 100 or Target"
              className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-fg outline-none focus:border-focus focus:ring-1 focus:ring-focus"
            />
          </div>
        )}

        {/* Format Selection */}
        {calculationType !== 'CUSTOM' && (
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="card-format-select" className="font-semibold text-fg">
                {t('summaryCards.numberFormat')}
              </label>
              <select
                id="card-format-select"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-bg px-2.5 text-fg outline-none focus:border-focus focus:ring-1 focus:ring-focus"
              >
                <option value="integer">{t('summaryCards.formatInteger')}</option>
                <option value="decimal">{t('summaryCards.formatDecimal')}</option>
                <option value="currency">{t('summaryCards.formatCurrency')}</option>
                <option value="percent">{t('summaryCards.formatPercent')}</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="card-decimals-input" className="font-semibold text-fg">
                {t('summaryCards.decimalPlaces')}
              </label>
              <input
                id="card-decimals-input"
                type="number"
                min="0"
                max="10"
                value={decimals}
                onChange={(e) => setDecimals(e.target.value)}
                placeholder={t('common.auto')}
                className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-fg outline-none focus:border-focus focus:ring-1 focus:ring-focus"
              />
            </div>
          </div>
        )}

        {/* Prefix and Suffix */}
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="card-prefix-input" className="font-semibold text-fg">
              {t('summaryCards.prefix')}
            </label>
            <input
              id="card-prefix-input"
              type="text"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              placeholder="e.g. $ or ~"
              className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-fg outline-none focus:border-focus focus:ring-1 focus:ring-focus"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="card-suffix-input" className="font-semibold text-fg">
              {t('summaryCards.suffix')}
            </label>
            <input
              id="card-suffix-input"
              type="text"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
              placeholder="e.g. USD or units"
              className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-fg outline-none focus:border-focus focus:ring-1 focus:ring-focus"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
}
